import { useState } from 'react';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';
import { PhotoUpload } from '../../components/PhotoUpload';
import { OrgPicker } from './OrgPicker';
import {
  PERSON_TEAM,
  PERSON_PARTNER,
  PERSON_EXTERNAL,
} from '../../lib/constants';

const TYPES = [PERSON_EXTERNAL, PERSON_TEAM, PERSON_PARTNER];

// Reusable add-person modal. The inline edit form in PeopleList shares the
// same field shape but uses a different submit path (PATCH vs POST).
export function CreatePersonModal({ onClose, onCreated, organisations, onOrgsRefresh }) {
  const { t } = useTranslation();
  const [photoB64, setPhotoB64] = useState(null);
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [organisationId, setOrganisationId] = useState(null);
  const [type, setType] = useState(PERSON_EXTERNAL);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [instagram, setInstagram] = useState('');
  const [facebook, setFacebook] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !submitting;

  function buildSocialHandles() {
    const handles = {};
    if (linkedin.trim())  handles.linkedin  = linkedin.trim();
    if (instagram.trim()) handles.instagram = instagram.trim();
    if (facebook.trim())  handles.facebook  = facebook.trim();
    return handles;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const created = await api.people.create({
        name: trimmedName,
        role: role.trim() || null,
        organisationId: organisationId || null,
        type,
        photoB64: photoB64 || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        socialHandles: buildSocialHandles(),
        notes: notes.trim() || null,
      });
      onCreated?.(created);
      onClose();
    } catch (err) {
      setError(err?.message || t('people.form.error'));
    } finally {
      setSubmitting(false);
    }
  }

  function handleBackdropClick() {
    if (!submitting) onClose();
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={handleBackdropClick}>
      <form
        className="modal modal--form"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        noValidate
      >
        <h2 className="modal__title">{t('people.form.createTitle')}</h2>

        {/* ── Section 1: Identity ─────────────────────────────────────── */}
        <div className="form-section">
          <h3 className="form-section__title">{t('people.form.sections.identity')}</h3>

          <div className="person-photo-row">
            <PhotoUpload value={photoB64} onChange={setPhotoB64} shape="circle" size={80} />
            <label className="form-field person-photo-row__field">
              <span className="form-field__label">{t('people.form.name')} *</span>
              <input
                className="form-field__input"
                type="text"
                value={name}
                placeholder={t('people.form.namePlaceholder')}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                required
              />
            </label>
          </div>

          <div className="modal__row">
            <label className="form-field">
              <span className="form-field__label">{t('people.form.role')}</span>
              <input
                className="form-field__input"
                type="text"
                value={role}
                placeholder={t('people.form.rolePlaceholder')}
                onChange={(e) => setRole(e.target.value)}
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">{t('people.form.type')}</span>
              <select
                className="form-field__select"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {TYPES.map((typeKey) => (
                  <option key={typeKey} value={typeKey}>{t(`people.types.${typeKey}`)}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {/* ── Section 2: Organisation ─────────────────────────────────── */}
        <div className="form-section">
          <h3 className="form-section__title">{t('people.form.sections.organisation')}</h3>
          <div className="form-field">
            <span className="form-field__label">{t('people.form.organisation')}</span>
            <OrgPicker
              value={organisationId}
              organisations={organisations}
              onChange={setOrganisationId}
              onCreated={onOrgsRefresh}
            />
          </div>
        </div>

        {/* ── Section 3: Contact ──────────────────────────────────────── */}
        <div className="form-section">
          <h3 className="form-section__title">{t('people.form.sections.contact')}</h3>
          <div className="modal__row">
            <label className="form-field">
              <span className="form-field__label">{t('people.form.email')}</span>
              <input
                className="form-field__input"
                type="email"
                value={email}
                placeholder={t('people.form.emailPlaceholder')}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">{t('people.form.phone')}</span>
              <input
                className="form-field__input"
                type="tel"
                value={phone}
                placeholder={t('people.form.phonePlaceholder')}
                onChange={(e) => setPhone(e.target.value)}
              />
            </label>
          </div>
        </div>

        {/* ── Section 4: Social ───────────────────────────────────────── */}
        <div className="form-section">
          <h3 className="form-section__title">{t('people.form.sections.social')}</h3>
          <div className="form-field__stack">
            <div className="social-input">
              <span className="social-input__icon" aria-hidden="true">💼</span>
              <input
                className="form-field__input"
                type="text"
                value={linkedin}
                placeholder={t('people.form.linkedinPlaceholder')}
                onChange={(e) => setLinkedin(e.target.value)}
              />
            </div>
            <div className="social-input">
              <span className="social-input__icon" aria-hidden="true">📷</span>
              <input
                className="form-field__input"
                type="text"
                value={instagram}
                placeholder={t('people.form.instagramPlaceholder')}
                onChange={(e) => setInstagram(e.target.value)}
              />
            </div>
            <div className="social-input">
              <span className="social-input__icon" aria-hidden="true">👍</span>
              <input
                className="form-field__input"
                type="text"
                value={facebook}
                placeholder={t('people.form.facebookPlaceholder')}
                onChange={(e) => setFacebook(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ── Section 5: Context ──────────────────────────────────────── */}
        <div className="form-section">
          <h3 className="form-section__title">{t('people.form.sections.context')}</h3>
          <label className="form-field">
            <span className="form-field__label">{t('people.form.notes')}</span>
            <textarea
              className="form-field__textarea"
              value={notes}
              placeholder={t('people.form.notesPlaceholder')}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </label>
        </div>

        <div className="gdpr-note">{t('people.gdprNote')}</div>

        {error && <div className="modal__error" role="alert">{error}</div>}

        <div className="modal__actions">
          <button
            type="button"
            className="button button--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="button button--primary" disabled={!canSubmit}>
            {submitting ? t('people.form.submitting') : t('people.form.submitCreate')}
          </button>
        </div>
      </form>
    </div>
  );
}
